import { FormattedMessage, useIntl } from 'react-intl'

import BaseCompactCard from './BaseCompactCard'

import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { useAirport } from 'tripPlanner/hooks/api/flights'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import { isFlightSearchBookmark } from 'tripPlanner/utils'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'

function getAirportName(
  airport: App.AirportLocation | undefined,
  placeName: string,
) {
  let airportName: string | undefined

  if (airport) {
    airportName =
      airport.airportName || airport.cityAirportName || airport.cityDesc
  }
  // Fallback if no airport or if it's missing data
  if (!airportName) {
    airportName = placeName
  }
  return airportName
}

interface Props {
  item: FlightItem
  presentationType: 'start' | 'end' | 'intermediary'
  variant: DetailItemCardVariant
}

function CompactFlightCard({ item, presentationType, variant }: Props) {
  const intl = useIntl()
  let description: string | undefined

  const { startTime, endTime } = item

  const { data: departureAirport, isLoading: isDepartureAirportLoading } =
    useAirport({
      code: item.originAirportCode,
    })

  const { data: arrivalAirport, isLoading: isArrivalAirportLoading } =
    useAirport({
      code: item.destinationAirportCode,
    })

  const departureAirportName = getAirportName(
    departureAirport,
    item.startPlace.name,
  )
  const arrivalAirportName = getAirportName(arrivalAirport, item.endPlace.name)
  const hasAirportNames = !!(departureAirportName && arrivalAirportName)
  const isLoadingAirportData =
    isDepartureAirportLoading || isArrivalAirportLoading

  const title = hasAirportNames ? (
    <FormattedMessage
      id="EwH4AzrKSgyGVN4Gvyw8"
      defaultMessage="{departure} {icon} {arrival}"
      description="Flight route showing departure and arrival airport names with an arrow icon between them"
      values={{
        departure: departureAirportName,
        icon: <LineArrowRightIcon size="S" />,
        arrival: arrivalAirportName,
      }}
    />
  ) : isLoadingAirportData ? (
    <TextLoadingBox width={160} />
  ) : (
    intl.formatMessage({
      id: 'rAX7QJGr9zA8liwvxUIJ',
      defaultMessage: 'Flight',
      description: 'Fallback title for a flight card when airport names are not available',
    })
  )

  const timeFormat = useRegionTimeFormat()

  const isMultiDay =
    item.endDate && item.endDate.diff(item.startDate, 'days') > 0

  if (presentationType === 'start') {
    if (startTime) {
      description = `Departs ${startTime.format(timeFormat)} (local time)`
    }
    if (!isMultiDay && startTime && endTime) {
      description = intl.formatMessage(
        {
          id: 'Obe-Lw6DN32v3MbR9VP0',
          defaultMessage: 'Departs {departureTime} (local time) · Arrives {arrivalTime} (local time)',
          description: 'Flight compact card label showing local departure and arrival times',
        },
        {
          departureTime: startTime.format(timeFormat),
          arrivalTime: endTime.format(timeFormat),
        },
      )
    }
    if (isMultiDay && !startTime && !isFlightSearchBookmark(item)) {
      description = intl.formatMessage({
        id: 'y14U_96ikoTFd_SkgKeN',
        defaultMessage: 'Departure',
        description: 'Description label for a multi-day flight on the departure day',
      })
    }
  } else if (presentationType === 'intermediary') {
    description = intl.formatMessage({
      id: 'cNC_36ESkOJK0JbrdbI7',
      defaultMessage: 'In transit',
      description: 'Description label for a flight on intermediary transit days',
    })
  } else if (presentationType === 'end') {
    if (endTime) {
      description = `Arrives ${endTime.format(timeFormat)} (local time)`
    } else {
      description = intl.formatMessage({
        id: 'X4fkzt1UQdnMoG1RGw1c',
        defaultMessage: 'Arrival',
        description: 'Description label for a flight on the arrival day without a specific time',
      })
    }
  }

  return (
    <BaseCompactCard
      item={item}
      icon={<LinePlaneDepartureIcon size="S" />}
      title={title}
      description={description}
      isPrimary={presentationType === 'start'}
      variant={variant}
    />
  )
}

export default CompactFlightCard
